import type { Cart } from "@/lib/api";

/** What the checkout page adds up. Mirrors OrderService.Checkout. */
export interface CheckoutTotals {
  subtotalIdr: number;
  discountIdr: number;
  /** Every desk's crate at full price, when crated. */
  packingKayuIdr: number;
  /** The part of packingKayuIdr the promotion waives. */
  promoPackingIdr: number;
  shippingIdr: number;
  shippingSubsidyIdr: number;
  /** The free gift's value. Shown, never charged. */
  promoGiftIdr: number;
  totalIdr: number;
}

/**
 * The same arithmetic checkout charges, from what the cart was served with:
 *
 *   total = subtotal − discount + packing kayu − packing waived
 *           + shipping − subsidy
 *
 * A promotion that waives packing kayu crates every desk whether or not the
 * box is ticked, so `packingTicked` only matters without one. The subsidy is
 * min(shipping, cap), once per order. The server decides for real; this is
 * so the number on screen is the number charged.
 */
export function checkoutTotals(
  cart: Cart | null,
  opts: { discountIdr?: number; shippingIdr?: number; packingTicked?: boolean },
): CheckoutTotals {
  const subtotalIdr = cart?.subtotalIdr ?? 0;
  const discountIdr = opts.discountIdr ?? 0;
  const shippingIdr = opts.shippingIdr ?? 0;
  const promo = cart?.promo;

  let packingKayuIdr = 0;
  let promoPackingIdr = 0;
  if (promo?.packingKayu) {
    packingKayuIdr = promo.packingKayu.totalIdr;
    promoPackingIdr = packingKayuIdr;
  } else if (opts.packingTicked && cart?.packingKayu) {
    packingKayuIdr = cart.packingKayu.totalIdr;
  }

  const shippingSubsidyIdr = promo && shippingIdr > 0 ? Math.min(shippingIdr, promo.shippingSubsidyMaxIdr) : 0;
  const promoGiftIdr = promo?.gift ? promo.gift.qty * promo.gift.unitPriceIdr : 0;

  return {
    subtotalIdr,
    discountIdr,
    packingKayuIdr,
    promoPackingIdr,
    shippingIdr,
    shippingSubsidyIdr,
    promoGiftIdr,
    totalIdr: subtotalIdr - discountIdr + packingKayuIdr - promoPackingIdr + shippingIdr - shippingSubsidyIdr,
  };
}
